import { Box, Button, FormControl, Grid, OutlinedInput, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion } from 'framer-motion';
import parse from 'html-react-parser';
import React from 'react';
import { useTranslation } from 'react-i18next';
import providers from '../../../constants/providers';

const ProvidersSection = ({ padding, backgroundColor }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const maxIcons = 16;

  const filterMaxNumIcons = (list) => {
    const included = [];
    const excluded = [];
    list.forEach((item, index) => {
      if (index < maxIcons) {
        included.push(item);
      } else {
        excluded.push(item);
      }
    });
    return { included, excluded };
  };

  const getFilteredProvidersBySlug = (slug) => {
    return filteredProvidersInitial.filter((provider) => {
      if (slug === 'all') {
        return true;
      }

      if (Array.isArray(provider.slug)) {
        return provider.slug.includes(slug);
      }

      return provider.slug && provider.slug === slug;
    })
  };

  const getFilteredProvidersByKeyword = (keyword) => {
    return filteredProvidersInitial.filter((provider) => {
      if (provider.name) {
        return provider.name.toLowerCase().includes(keyword.toLowerCase());
      }
      return false;
    });
  };

  // Filter the providers array before setting the initial state
  const filteredProvidersInitial = providers;
  const { included: initIncluded, excluded: initExcluded } = filterMaxNumIcons(filteredProvidersInitial);

  const [selectedProvider, setSelectedProvider] = React.useState('all');
  const [filteredProviders, setfilteredProviders] = React.useState(initIncluded);
  const [isShownAllIcons, setIsShownAllIcons] = React.useState(false);
  const [excludedIcons, setExcludedIcons] = React.useState(initExcluded);
  const label = t('provider-content-desktop');
  const labelMobile = t('provider-content-desktop');
  const header = t('provider-title-desktop');
  const headerMobile = t('provider-title-mobile');
  const handleSelectProvider = (slug) => {
    setSelectedProvider(slug);
    const filtered = getFilteredProvidersBySlug(slug);
    const { included, excluded } = filterMaxNumIcons(filtered);
    setfilteredProviders(included);
    setExcludedIcons(excluded);
    setIsShownAllIcons(false);
  }

  const handleSearchProviderByKeyword = (event) => {
    const keyword = event.target.value;
    setSelectedProvider('all');
    const filtered = getFilteredProvidersByKeyword(keyword);
    const { included, excluded } = filterMaxNumIcons(filtered);
    setfilteredProviders(included);
    setExcludedIcons(excluded);
    setIsShownAllIcons(false);
  }

  const toggleExcludedIcons = () => {
    if (!isShownAllIcons) {
      setfilteredProviders(filteredProviders.concat(excludedIcons));
    } else {
      const { included } = filterMaxNumIcons(filteredProviders.concat(excludedIcons));
      setfilteredProviders(included);
    }
    setIsShownAllIcons(!isShownAllIcons);
  }

  const activeProviderButtonStyle = {
    variant: "contained",
    sx: {
      color: '#FFFFFF',
      backgroundColor: '#00AA2F',
      borderRadius: '50px',
      width: '120.39px',
      height: '45px',
      marginRight: '30px',
      [theme.breakpoints.down('lg')]: {
        width: '120px',
        height: '45px',
        fontSize: '16px',
      },
    }
  }

  const defaultProviderButtonStyle = {
    variant: "outlined",
    sx: {
      color: '#000000',
      borderColor: '#00AA2F',
      borderRadius: '50px',
      width: '120.39px',
      height: '45px',
      marginRight: '30px',
      '&:hover': {
        color: '#FFFFFF',
        backgroundColor: '#00AA2F',
      },
    }
  }

  return (
    <Box style={{ padding: padding, backgroundColor: backgroundColor, width: '100%' }}>
      <Box
        sx={{
          padding: '0 20%',
          [theme.breakpoints.down('lg')]: {
            padding: '0 5%',
          },
        }}
      >
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: '48px',
            fontWeight: '700',
            mb: 4,
            color: '#000000',
            lineHeight: '57.28px',
            textAlign: 'center',
            [theme.breakpoints.down('lg')]: {
              fontSize: '32px',
              lineHeight: '38.19px',
            },
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 100,
            }}
            whileInView={{
              opacity: 1,
              y: 0, // Slide in to its original position
              transition: {
                duration: 1, // Animation duration
              },
            }}
          >
            {!isMobile ? parse(header) : parse(headerMobile)}
          </motion.div>
        </Typography>
      </Box>
      <Box
        sx={{
          padding: '5px 15%',
          [theme.breakpoints.down('lg')]: {
            padding: '0px 5%',
          },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.8,
            wordWrap: 'break-word',
            fontWeight: localStorage.getItem('i18nextLng') === 'en-US' ? '600' : '700',
            fontSize: '20px',
            color: '#000000',
            lineHeight: '23.87px',
            textAlign: 'center',
            [theme.breakpoints.down('lg')]: {
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '21.48px',
            },
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 100,
            }}
            whileInView={{
              opacity: 1,
              y: 0, // Slide in to its original position
              transition: {
                duration: 1, // Animation duration
                delay: 0.8,
              },
            }}
          >
            {!isMobile ? parse(label) : parse(labelMobile)}
          </motion.div>
        </Typography>
      </Box >
      <Box
        mt={8}
        display={'flex'}
        justifyContent={'center'}
        sx={{
          padding: '0px 3%',
          [theme.breakpoints.down('lg')]: {
            padding: '0',
          },
        }}
      >
        <Grid
          container
          spacing={2}
          display={'flex'}
          sx={{
            justifyContent: 'space-between',
            [theme.breakpoints.down('lg')]: {
              justifyContent: 'center',
              marginLeft: 'unset',
            },
          }}
        >
          <Grid size={8}>
            <Box
              sx={{
                textAlign: 'left',
                [theme.breakpoints.down('lg')]: {
                  textAlign: 'center',
                },
              }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#2B663C',
                  marginRight: '30px',
                  lineHeight: "42.96pxx",
                  [theme.breakpoints.down('lg')]: {
                    fontSize: '30px',
                    lineHeight: '35.8px',
                    textAlign: 'center',
                    marginRight: 'unset',
                    mb: 5,
                  },
                }}
              >
                {t('provider-label-one')}
              </Typography>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: '20px',
                  fontWeight: '700',
                  mb: 4,
                  color: '#000000',
                  textAlign: 'left !important',
                  lineHeight: "23.87px",
                  [theme.breakpoints.down('lg')]: {
                    display: 'none',
                  },
                }}
              >
                {t('provider-label-two')}
              </Typography>
            </Box>
          </Grid>
          <Grid
            sx={{
              // marginRight: 18,
              [theme.breakpoints.down('lg')]: {
                display: 'flex',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }, // Hide scrollbar
                flexGrow: 1,
                padding: '0 10px',
                width: '100%',
              },
            }}
          >
            <Box
              sx={{
                [theme.breakpoints.down('lg')]: {
                  display: 'none',
                },
              }}
            >
              <Button
                {...(selectedProvider === 'all' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('all')}
              >
                {t('provider-filter-one')}
              </Button>
              <Button
                {...(selectedProvider === 'casino' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('casino')}
              >
                {t('provider-filter-two')}
              </Button>
              <Button
                {...(selectedProvider === 'slots' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('slots')}
              >
                {t('provider-filter-three')}
              </Button>
              <Button
                {...(selectedProvider === 'sports' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('sports')}
              >
                {t('provider-filter-four')}
              </Button>
              <FormControl
                sx={{ width: '25ch', display: { xs: 'none', md: 'none', lg: 'inline', xl: 'inline' } }}
              >
                <OutlinedInput
                  placeholder={t('provider-filter-five')}
                  sx={{
                    border: '1px solid #D7D7D7',
                    borderRadius: '4px',
                    borderColor: '#D7D7D7',
                    width: '250px',
                    height: '40px',
                    padding: '0px 17px',
                    color: '#000000',
                  }}
                  onKeyUp={(event) => {
                    handleSearchProviderByKeyword(event);
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
          <Grid
            container
            spacing={1}
            sx={{
              display: 'none',
              [theme.breakpoints.down('lg')]: {
                display: 'flex',
              },
            }}
          >
            <Grid item xs={4}>
              <Button
                style={{ width: '100%', height: '100%', margin: '0' }}
                {...(selectedProvider === 'all' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('all')}
              >
                {t('provider-filter-one')}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                style={{ width: '100%', height: '100%', margin: '0' }}
                {...(selectedProvider === 'casino' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('casino')}
              >
                {t('provider-filter-two')}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                style={{ width: '100%', height: '100%', margin: '0' }}
                {...(selectedProvider === 'slots' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('slots')}
              >
                {t('provider-filter-three')}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                style={{ width: '100%', height: '100%', margin: '0' }}
                {...(selectedProvider === 'sports' ? activeProviderButtonStyle : defaultProviderButtonStyle)}
                onClick={() => handleSelectProvider('sports')}
              >
                {t('provider-filter-four')}
              </Button>
            </Grid>
          </Grid>
          <Grid container width={'100%'}
            sx={{
              mt: 2,
              mb: 2,
            }}
          >
            <Box justifyContent={'center'} width={'100%'} display={'flex'}>
              <FormControl
                sx={{
                  width: '100%',
                  display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none', xl: 'none' },
                  marginTop: 8,
                  [theme.breakpoints.down('lg')]: {
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 2,
                  },
                }}
                onKeyUp={(event) => {
                  handleSearchProviderByKeyword(event);
                }}
              >
                <OutlinedInput
                  placeholder={t('provider-filter-five')}
                  sx={{
                    border: '1px solid #D7D7D7',
                    borderRadius: '4px',
                    borderColor: '#D7D7D7',
                    height: '49px',
                    padding: '0px 17px',
                    color: '#000000',
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Box >
      <Box
        mb={4}
        display={'flex'}
        justifyContent={'center'}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          color: '#000000',
          [theme.breakpoints.down('lg')]: {
          },
        }}
      >
        <Grid container spacing={2}>
          {filteredProviders.map((item, index) => (
            <Grid item xs={4} sm={3} md={1.5} key={index}>
              <Box sx={{ maxWidth: '100%', maxHeight: '100%', mt: 5 }} display={'flex'} key={item.id}>
                <Box
                  sx={{
                    // backgroundColor: '#ffffff',
                    textAlign: 'center',
                    paddingLeft: 1,
                    paddingRight: 1,
                    maxWidth: '628px',
                    maxHeight: '100%',
                    [theme.breakpoints.down('lg')]: {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 'auto',
                      [theme.breakpoints.down('lg')]: {
                        width: '100%',
                        height: 'auto',
                      },
                    }}
                  >
                    {item.image}
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box
        justifyContent={'center'}
        display={'flex'}
        width={'100%'}
        sx={{
          mb: 5,
          [theme.breakpoints.down('lg')]: {
            mb: 5,
          },
        }}
      >
        {!isShownAllIcons && excludedIcons.length ? (
          <Button
            variant="outlined"
            sx={{
              color: '#000000',
              borderColor: '#00AA2F',
              borderRadius: '50px',
              width: '120.39px',
              height: '45px',
              fontWeight: 800,
              fontSize: '16px',
              '&:hover': {
                color: '#FFFFFF',
                backgroundColor: '#00AA2F',
              },
            }}
            onClick={() => toggleExcludedIcons()}
          >
            {t('provider-filter-six')}
          </Button>
        ) : null}

        {isShownAllIcons ? (
          <Button
            variant="outlined"
            sx={{
              color: '#000000',
              borderColor: '#00AA2F',
              borderRadius: '50px',
              width: '120.39px',
              height: '45px',
              fontWeight: 800,
              fontSize: '16px',
              '&:hover': {
                color: '#FFFFFF',
                backgroundColor: '#00AA2F',
              },
            }}
            onClick={() => toggleExcludedIcons()}
          >
            {t('provider-filter-seven')}
          </Button>
        ) : null}

      </Box>
    </Box>
  );
}

export default ProvidersSection;